import { Candidate, Tontine } from '@/models';


/**
 * Calcule le total des charges mensuelles pour tous les types de candidats
 * @param data - Données du candidat
 * @returns Total des charges mensuelles en FCFA
 */
export const calculateTotalCharges = (data: Partial<Candidate>): number => {
  const {
    loyer_actuel = 0,
    electricite = 0,
    eau = 0,
    scolarite_enfants = 0,
    aide_famille = 0,
    transport = 0,
    nourriture = 0,
    autres_charges = 0,
  } = data;

  // Calcul des tontines converties en montant mensuel
  const totalTontines = (data.tontines || []).reduce((sum: number, t: Tontine) => {
    const montantMensuel =
      t.frequence === 'Quotidien' ? Number(t.montant) * 26 :
      t.frequence === 'Hebdomadaire' ? Number(t.montant) * 4 :
      Number(t.montant);
    return sum + (montantMensuel || 0);
  }, 0);

  // Calcul des mensualités de crédits
  const totalCredits = ((data.credits as any[]) || []).reduce<number>((sum: number, c: any) =>
    sum + (Number(c.mensualite) || 0), 0
  );

  return (
    Number(loyer_actuel || 0) +
    Number(electricite || 0) +
    Number(nourriture|| 0)+
    Number(eau || 0) +
    Number(scolarite_enfants || 0) +
    Number(aide_famille || 0) +
    Number(transport || 0) +
    Number(autres_charges || 0) +
    totalTontines +
    totalCredits
  );
};

/**
 * Calcule le total des revenus mensuels pour les employés
 * @param data - Données du candidat employé
 * @returns Total des revenus mensuels en FCFA
 */
export const calculateTotalRevenuesEmployee = (data: Partial<Candidate>): number => {
  const {
    monthly_income=0,
    salaire_base_net = 0,
    indemnite_transport = 0,
    indemnite_logement = 0,
    autres_indemnites = 0,
    primes_variables_moyenne = 0,
    commerce_parallele = 0,
    revenus_locatifs = 0,
    transferts_diaspora = 0,
    autres_revenus = 0
  } = data;

  return (
    Number(monthly_income || 0) +
    Number(salaire_base_net || 0) +
    Number(indemnite_transport || 0) +
    Number(indemnite_logement || 0) +
    Number(autres_indemnites || 0) +
    Number(primes_variables_moyenne || 0) +
    Number(commerce_parallele || 0) +
    Number(revenus_locatifs || 0) +
    Number(transferts_diaspora || 0) +
    Number(autres_revenus || 0)
  );
};

/**
 * Calcule le total des revenus mensuels pour les entrepreneurs
 * @param data - Données du candidat entrepreneur
 * @returns Total des revenus mensuels en FCFA
 */
export const calculateTotalRevenuesEntrepreneur = (data: Partial<Candidate>): number => {
  const {
    revenu_personnel_tire = 0,
    revenus_locatifs = 0,
    autres_revenus = 0
  } = data;

  return (
    Number(revenu_personnel_tire || 0) +
    Number(revenus_locatifs || 0) +
    Number(autres_revenus || 0)
  );
};

/**
 * Calcule le total des revenus mensuels pour tous les types de candidats
 * Détecte automatiquement le type de candidat et utilise la formule appropriée
 * @param data - Données du candidat
 * @returns Total des revenus mensuels en FCFA
 */
export const calculateTotalRevenues = (data: Partial<Candidate>): number => {
  // Détection du type de candidat basée sur les champs présents
  const hasEmployeeFields = data.salaire_base_net !== undefined ||
                            data.indemnite_transport !== undefined;

  const hasEntrepreneurFields = data.revenu_personnel_tire !== undefined;

  if (hasEmployeeFields) {
    return calculateTotalRevenuesEmployee(data);
  } else if (hasEntrepreneurFields) {
    return calculateTotalRevenuesEntrepreneur(data);
  }

  // Fallback: essayer de calculer tous les revenus possibles
  return calculateTotalRevenuesEmployee(data) + calculateTotalRevenuesEntrepreneur(data);
};

/**
 * Calcule le total de l'épargne disponible
 * @param data - Données du candidat
 * @returns Total de l'épargne en FCFA
 */
export const calculateTotalEpargne = (data: Partial<Candidate>): number => {
  const {
    epargne_bancaire = 0,
    epargne_mobile_money = 0,
    epargne_informelle = 0
  } = data;

  return (
    Number(epargne_bancaire || 0) +
    Number(epargne_mobile_money || 0) +
    Number(epargne_informelle || 0)
  );
};

/**
 * Calcule le reste à vivre (RAV)
 * @param revenus - Total des revenus mensuels
 * @param charges - Total des charges mensuelles
 * @returns Reste à vivre en FCFA
 */
export const calculateRAV = (revenus: number, charges: number): number => {
  return Math.max(0, revenus - charges);
};

/**
 * Calcule le taux d'effort (pourcentage des revenus consacré au loyer)
 * @param loyer - Montant du loyer
 * @param revenus - Total des revenus mensuels
 * @returns Taux d'effort en pourcentage (0-100)
 */
export const calculateTauxEffort = (loyer: number, revenus: number): number => {
  if (revenus === 0) return 0;
  return (loyer / revenus) * 100;
};

/**
 * Calcule la capacité d'épargne mensuelle
 * @param revenus - Total des revenus mensuels
 * @param charges - Total des charges mensuelles
 * @param loyerCible - Loyer du logement ciblé (optionnel)
 * @returns Capacité d'épargne mensuelle en FCFA
 */
export const calculateCapaciteEpargne = (
  revenus: number,
  charges: number,
  loyerCible?: number
): number => {
  const chargesWithNewRent = loyerCible !== undefined
    ? charges + loyerCible
    : charges;

  return Math.max(0, revenus - chargesWithNewRent);
};
